import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { Link } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ApiError, companyApi, uploadFileToSignedUrl } from '../lib/api.ts';
import { Btn } from '../components/Btn.tsx';
import { CenteredSpinner } from '../components/Spinner.tsx';
import { SubmissionStatusBadge } from '../components/StatusBadge.tsx';
import { ErrorMessage } from '../components/ErrorMessage.tsx';
import { FileDrop } from '../components/FileDrop.tsx';
import { Field, Select, TextArea, TextInput } from '../components/Field.tsx';
import { EmptyState } from '../components/EmptyState.tsx';
import type {
  Company,
  CompanyDocument,
  Json,
  Question,
  QuestionCategory,
  Submission,
} from '../lib/types.ts';
import { activeAdoptionTargets, computeProgress, questionCompletion } from '../lib/visibility.ts';
import { blockerHint, stepBlockers } from '../lib/stepBlockers.ts';
import { applyLocalAnswers, buildSteps, type LocalAnswer, type Step } from '../lib/wizard.ts';
import { missingCompanyInfoFields, REQUIRED_COMPANY_INFO_FIELDS } from '@l2u/shared/progress';
import { isAnswerLocked } from '@l2u/shared';
import { useBranding } from '../lib/branding.tsx';
import { expiryState, fromDateInput, toDateInput } from '../lib/expiry.ts';
import type { SubmissionFile } from '../lib/types.ts';
import { companyInfoLabel } from '../lib/companyInfo.ts';
import { instanceOptionLabel } from '../lib/instanceOption.ts';
import { useAbnLookup } from '../lib/useAbnLookup.ts';
import { AbnStatusNote } from './CompanyInfo.tsx';
import { WizardContext, useWizardCtx } from './wizardContext.tsx';

type QuestionnaireWizardProps = {
  apiPrefix?: string;
  meQueryKey?: readonly unknown[];
  submitButtonLabel?: string;
  // Optional "Exit and save for later" target. When set, renders a centred
  // link below the form footer. Progress is saved on every answer change, so
  // exiting just leaves the page.
  exitTo?: string;
  // Optional extra completion gate (e.g. missing company info fields) that
  // disables the Submit button on the last step and renders a custom hint.
  extraGate?: { count: number; render: () => React.ReactNode } | null;
  // When true, prepends a "Company information" step that captures the
  // company's required profile fields before the questionnaire categories.
  includeCompanyInfoStep?: boolean;
};

export function Questionnaire() {
  return <QuestionnaireWizard apiPrefix="/api/me" meQueryKey={['me']} includeCompanyInfoStep />;
}

export function QuestionnaireWizard({
  apiPrefix = '/api/me',
  meQueryKey = ['me'],
  submitButtonLabel = 'Submit for review',
  exitTo,
  extraGate = null,
  includeCompanyInfoStep = false,
}: QuestionnaireWizardProps) {
  const qc = useQueryClient();
  const submissionsKey = [...meQueryKey, 'submissions'] as const;

  const meQuery = useQuery({
    queryKey: meQueryKey,
    queryFn: () => companyApi.get<{ company: Company }>(apiPrefix),
    enabled: true,
  });
  const questionsQuery = useQuery({
    queryKey: [`${apiPrefix}/questions`],
    queryFn: () => companyApi.get<{ questions: Question[] }>(`${apiPrefix}/questions`),
  });
  const submissionsQuery = useQuery({
    queryKey: submissionsKey,
    queryFn: () => companyApi.get<{ submissions: Submission[] }>(`${apiPrefix}/submissions`),
  });
  const documentsQuery = useQuery({
    queryKey: [`${apiPrefix}/documents`],
    queryFn: () => companyApi.get<{ documents: CompanyDocument[] }>(`${apiPrefix}/documents`),
  });
  const categoriesQuery = useQuery({
    queryKey: [`${apiPrefix}/question-categories`],
    queryFn: () =>
      companyApi.get<{ categories: QuestionCategory[] }>(`${apiPrefix}/question-categories`),
  });

  const submitMutation = useMutation({
    mutationFn: () => companyApi.post(`${apiPrefix}/submit`),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: meQueryKey });
      void qc.invalidateQueries({ queryKey: submissionsKey });
    },
  });

  const questions = questionsQuery.data?.questions ?? [];
  const submissions = submissionsQuery.data?.submissions ?? [];
  const documents = documentsQuery.data?.documents ?? [];
  const categories = categoriesQuery.data?.categories ?? [];
  const company = meQuery.data?.company;
  const ownerData = meQuery.data as
    | { company?: { submitted_at?: string | null }; driver?: { submitted_at?: string | null }; asset?: { submitted_at?: string | null } }
    | undefined;
  const submittedAt =
    ownerData?.company?.submitted_at ??
    ownerData?.driver?.submitted_at ??
    ownerData?.asset?.submitted_at ??
    null;

  // Answers entered this session, overlaid on the server-fetched submissions so
  // the "Continue" gate reflects what the company just typed/selected the moment
  // they do it — not only after the PUT round-trips back. This is what stops a
  // freshly-answered question from being listed as "still required".
  const [localAnswers, setLocalAnswers] = useState<Map<string, LocalAnswer>>(() => new Map());
  const setLocalAnswer = useCallback(
    (questionId: string, answer: Json | null, comment: string | null) => {
      setLocalAnswers((prev) => {
        const next = new Map(prev);
        next.set(questionId, { answer, comment });
        return next;
      });
    },
    [],
  );
  const clearLocalAnswer = useCallback((questionId: string) => {
    setLocalAnswers((prev) => {
      if (!prev.has(questionId)) return prev;
      const next = new Map(prev);
      next.delete(questionId);
      return next;
    });
  }, []);

  const effectiveSubmissions = useMemo(
    () => applyLocalAnswers(submissions, localAnswers),
    [submissions, localAnswers],
  );

  const docMap = useMemo(() => new Map(documents.map((d) => [d.id, d])), [documents]);
  const subByQ = useMemo(() => {
    const m = new Map<string, Submission>();
    for (const s of effectiveSubmissions) m.set(s.question_id, s);
    return m;
  }, [effectiveSubmissions]);

  const questionById = useMemo(() => new Map(questions.map((x) => [x.id, x])), [questions]);

  const breakdown = useMemo(
    () => computeProgress(questions, effectiveSubmissions),
    [questions, effectiveSubmissions],
  );

  const extraGateCount = extraGate?.count ?? 0;
  const companyInfoMissingCount = company ? missingCompanyInfoFields(company).length : 0;

  const steps = useMemo<Step[]>(
    () => buildSteps(questions, categories, breakdown.visibleIds, includeCompanyInfoStep),
    [questions, categories, breakdown.visibleIds, includeCompanyInfoStep],
  );

  const [stepIndex, setStepIndex] = useState(0);
  // The required question to scroll to / flash when the user tries to advance
  // before completing the step.
  const [highlightId, setHighlightId] = useState<string | null>(null);

  // Clamp the active step whenever the visible step count changes (e.g. an
  // answer hides a follow-up category) so we never end up past the last step.
  useEffect(() => {
    if (steps.length === 0) return;
    if (stepIndex > steps.length - 1) setStepIndex(steps.length - 1);
  }, [steps.length, stepIndex]);

  // Drop the highlight when the active step changes.
  useEffect(() => {
    setHighlightId(null);
  }, [stepIndex]);

  function stepCompletion(step: Step): { required: number; complete: number } {
    if (step.kind === 'company-info') {
      const total = REQUIRED_COMPANY_INFO_FIELDS.length;
      return { required: total, complete: total - companyInfoMissingCount };
    }
    let required = 0;
    let complete = 0;
    for (const q of step.questions) {
      if (!q.required) continue;
      required += 1;
      const reason = breakdown.completionByQuestion.get(q.id);
      if (reason === null) complete += 1;
    }
    return { required, complete };
  }

  function stepLabel(step: Step): string {
    return step.kind === 'company-info' ? 'Company information' : step.category.name;
  }

  if (
    questionsQuery.isLoading ||
    submissionsQuery.isLoading ||
    documentsQuery.isLoading ||
    categoriesQuery.isLoading ||
    (includeCompanyInfoStep && meQuery.isLoading)
  ) {
    return <CenteredSpinner />;
  }

  if (steps.length === 0) {
    return (
      <EmptyState
        title="No questions yet"
        description="Check back once your administrator has set up the questionnaire."
      />
    );
  }

  const safeIndex = Math.min(stepIndex, steps.length - 1);
  const currentStep = steps[safeIndex]!;
  const isLastStep = safeIndex === steps.length - 1;
  const currentComplete = stepCompletion(currentStep);
  const canAdvance = currentComplete.complete >= currentComplete.required;

  // Required questions in the current step that are still incomplete, so we can
  // tell the user *what* to finish rather than just greying out the button.
  const blockers =
    currentStep.kind === 'questions'
      ? stepBlockers(currentStep.questions, breakdown.completionByQuestion)
      : [];
  const submitReady =
    canAdvance &&
    breakdown.progress >= 100 &&
    companyInfoMissingCount === 0 &&
    extraGateCount === 0;

  function revealBlocker(id: string) {
    setHighlightId(id);
    if (typeof document !== 'undefined') {
      document.getElementById(`q-${id}`)?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  }
  function revealFirstBlocker() {
    if (blockers[0]) revealBlocker(blockers[0].question.id);
  }
  // Index of the first step that still has something outstanding — used to send
  // the user back to it when they try to submit before finishing.
  function firstIncompleteStepIndex(): number {
    for (let i = 0; i < steps.length; i += 1) {
      const s = steps[i]!;
      if (s.kind === 'company-info') {
        if (companyInfoMissingCount > 0) return i;
      } else if (stepBlockers(s.questions, breakdown.completionByQuestion).length > 0) {
        return i;
      }
    }
    return -1;
  }
  function handleSubmitClick() {
    if (submitReady) {
      submitMutation.mutate();
      return;
    }
    if (!canAdvance) {
      revealFirstBlocker();
      return;
    }
    const idx = firstIncompleteStepIndex();
    if (idx >= 0) setStepIndex(idx);
  }

  return (
    <WizardContext.Provider
      value={{
        apiPrefix,
        meQueryKey,
        submissionsQueryKey: submissionsKey,
        setLocalAnswer,
        clearLocalAnswer,
      }}
    >
      <div className="space-y-5">
        <ExpiryBanner submissions={submissions} />

        <Stepper
          steps={steps}
          activeIndex={safeIndex}
          completionFor={stepCompletion}
          labelFor={stepLabel}
          onSelect={(i) => setStepIndex(i)}
        />

        <div className="flex items-center justify-between text-xs text-slate-500">
          <span>
            {breakdown.completedRequired}/{breakdown.totalRequired} required complete
          </span>
          <span className="font-medium">{breakdown.progress}%</span>
        </div>

        <section className="rounded-2xl border border-slate-200 bg-white p-6 space-y-4">
          <div>
            <div className="text-xs font-semibold uppercase tracking-wide text-orange-600">
              Step {safeIndex + 1} of {steps.length}
            </div>
            <h2
              className="mt-1 text-2xl font-bold tracking-tight"
              style={{ color: 'var(--brand-ink)' }}
            >
              {stepLabel(currentStep)}
            </h2>
          </div>
          {currentStep.kind === 'company-info' && company ? (
            <CompanyInfoStep
              company={company}
              isLastStep={isLastStep}
              onSaved={() => {
                const next = safeIndex + 1;
                // When company info is the only/last step (no questionnaire
                // questions apply), there is nothing to advance to — submit
                // instead of bouncing back to the same step.
                if (next < steps.length) setStepIndex(next);
                else submitMutation.mutate();
              }}
              meQueryKey={meQueryKey}
              apiPrefix={apiPrefix}
            />
          ) : currentStep.kind === 'questions' ? (
            currentStep.questions.map((q) => (
              <div
                key={q.id}
                id={`q-${q.id}`}
                className={`scroll-mt-24 ${
                  highlightId === q.id ? 'rounded-2xl ring-2 ring-amber-400 ring-offset-2' : ''
                }`}
              >
                <QuestionCard
                  question={q}
                  submission={subByQ.get(q.id)}
                  documents={docMap}
                  documentsList={documents}
                  instanceOption={instanceOptionLabel(q, questionById)}
                  submittedAt={submittedAt}
                />
              </div>
            ))
          ) : null}
        </section>

        {currentStep.kind !== 'company-info' && (
          <div>
            <div className="flex flex-wrap items-center justify-between gap-3">
              <Btn
                variant="secondary"
                disabled={safeIndex === 0}
                onClick={() => setStepIndex(Math.max(0, safeIndex - 1))}
                className="!rounded-lg !normal-case !tracking-normal !font-medium"
              >
                ← Back
              </Btn>

              {isLastStep ? (
                <Btn
                  onClick={handleSubmitClick}
                  loading={submitMutation.isPending}
                  className={`!rounded-lg !normal-case !tracking-normal !font-semibold ${
                    submitReady ? '' : 'opacity-60'
                  }`}
                  style={{ backgroundColor: 'var(--brand-primary)' }}
                >
                  {submitButtonLabel}
                </Btn>
              ) : (
                <Btn
                  onClick={() => (canAdvance ? setStepIndex(safeIndex + 1) : revealFirstBlocker())}
                  className={`!rounded-lg !normal-case !tracking-normal !font-semibold ${
                    canAdvance ? '' : 'opacity-60'
                  }`}
                  style={{ backgroundColor: 'var(--brand-primary)' }}
                >
                  Continue →
                </Btn>
              )}
            </div>

            <div className="mt-3 text-center text-xs text-slate-500">
              {currentComplete.required === 0
                ? 'No required questions in this step.'
                : `${currentComplete.complete}/${currentComplete.required} required answered in this step`}
            </div>

            {blockers.length > 0 && (
              <div className="mx-auto mt-3 max-w-md rounded-lg border border-amber-200 bg-amber-50 px-4 py-3">
                <div className="text-xs font-semibold text-amber-800">
                  Finish {blockers.length} required question
                  {blockers.length === 1 ? '' : 's'} to continue:
                </div>
                <ul className="mt-2 space-y-1">
                  {blockers.map((b) => (
                    <li key={b.question.id}>
                      <button
                        type="button"
                        onClick={() => revealBlocker(b.question.id)}
                        className="text-left text-xs text-amber-900 hover:underline"
                      >
                        • {b.question.label}{' '}
                        <span className="text-amber-700">— {blockerHint(b.reason)}</span>
                      </button>
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {extraGate && isLastStep && (
              <div className="mt-2 text-center">{extraGate.render()}</div>
            )}
            {submitMutation.error && (
              <div className="mt-3">
                <ErrorMessage error={submitMutation.error} />
              </div>
            )}
            {submitMutation.isSuccess && (
              <div className="mt-3 rounded-md border border-emerald-200 bg-emerald-50 px-3 py-2 text-sm text-emerald-700">
                Submitted for review.
              </div>
            )}

            {exitTo && (
              <div className="mt-6 text-center">
                <Link
                  to={exitTo}
                  className="text-sm text-slate-500 hover:text-slate-700 hover:underline"
                >
                  ← Exit and save for later
                </Link>
              </div>
            )}
          </div>
        )}
      </div>
    </WizardContext.Provider>
  );
}

function Stepper({
  steps,
  activeIndex,
  completionFor,
  labelFor,
  onSelect,
}: {
  steps: Step[];
  activeIndex: number;
  completionFor: (s: Step) => { required: number; complete: number };
  labelFor: (s: Step) => string;
  onSelect: (i: number) => void;
}) {
  function keyFor(step: Step, i: number): string {
    return step.kind === 'company-info' ? 'company-info' : `${step.category.id}-${i}`;
  }
  return (
    <ol className="flex w-full items-start">
      {steps.map((step, i) => {
        const c = completionFor(step);
        const done = c.required > 0 && c.complete >= c.required;
        const isActive = i === activeIndex;
        const isReached = i <= activeIndex || done;
        const circleStyle: React.CSSProperties = isActive
          ? { backgroundColor: 'var(--brand-primary)', color: '#fff' }
          : done
            ? { backgroundColor: 'var(--brand-primary)', color: '#fff', opacity: 0.85 }
            : { backgroundColor: '#E2E8F0', color: '#64748B' };
        const connectorStyle: React.CSSProperties = isReached
          ? { backgroundColor: 'var(--brand-primary)' }
          : { backgroundColor: '#E2E8F0' };
        const label = labelFor(step);

        return (
          <li
            key={keyFor(step, i)}
            className="relative flex flex-1 flex-col items-center"
            style={{ minWidth: 0 }}
          >
            {/* Connector to next step — positioned to span between circle centres */}
            {i < steps.length - 1 && (
              <span
                aria-hidden
                className="absolute top-4 h-0.5"
                style={{ left: '50%', right: '-50%', ...connectorStyle }}
              />
            )}
            <button
              type="button"
              onClick={() => onSelect(i)}
              className="relative z-10 flex h-8 w-8 items-center justify-center rounded-full text-sm font-semibold transition hover:opacity-90 focus:outline-none"
              style={circleStyle}
              aria-current={isActive ? 'step' : undefined}
              aria-label={`Step ${i + 1}: ${label}`}
            >
              {done && !isActive ? '✓' : i + 1}
            </button>
            <span
              className={`mt-2 px-1 text-center text-xs ${
                isActive ? 'font-semibold' : 'font-medium'
              }`}
              style={{
                color: isActive ? 'var(--brand-ink)' : '#64748B',
                wordBreak: 'break-word',
              }}
            >
              {label}
            </span>
          </li>
        );
      })}
    </ol>
  );
}

// ----------------------------------------------------------------------
// Company information step
// ----------------------------------------------------------------------

type CompanyInfoForm = {
  abn: string;
  trustee_info: string;
  primary_contact_name: string;
  contact_role: string;
  contact_phone: string;
  contact_email: string;
  employee_count: string;
  legal_entity_name: string;
  address_state: string;
  address_postcode: string;
  suburb: string;
};

function toCompanyInfoForm(c: Company): CompanyInfoForm {
  return {
    abn: c.abn ?? '',
    trustee_info: c.trustee_info ?? '',
    primary_contact_name: c.primary_contact_name ?? '',
    contact_role: c.contact_role ?? '',
    contact_phone: c.contact_phone ?? '',
    contact_email: c.contact_email ?? '',
    employee_count: c.employee_count === null ? '' : String(c.employee_count),
    legal_entity_name: c.legal_entity_name ?? '',
    address_state: c.address_state ?? '',
    address_postcode: c.address_postcode ?? '',
    suburb: c.suburb ?? '',
  };
}

function CompanyInfoStep({
  company,
  isLastStep,
  onSaved,
  meQueryKey,
  apiPrefix,
}: {
  company: Company;
  isLastStep: boolean;
  onSaved: () => void;
  meQueryKey: readonly unknown[];
  apiPrefix: string;
}) {
  const qc = useQueryClient();
  const { branding } = useBranding();
  const [form, setForm] = useState<CompanyInfoForm>(() => toCompanyInfoForm(company));
  const [error, setError] = useState<unknown>(null);

  // Re-seed the local form whenever the saved company record changes (e.g.
  // returning to this step after edits sync from another tab).
  useEffect(() => {
    setForm(toCompanyInfoForm(company));
  }, [company.id, company.updated_at]);

  const locked = company.status === 'review' || company.status === 'compliant';

  function set<K extends keyof CompanyInfoForm>(key: K, value: CompanyInfoForm[K]) {
    setForm((f) => ({ ...f, [key]: value }));
  }

  const post = useCallback(<T,>(p: string, b?: unknown) => companyApi.post<T>(p, b), []);
  const abnLookup = useAbnLookup(form.abn, post, '/api/me/abn-lookup');
  const lastFilled = useRef<string | null>(null);
  useEffect(() => {
    if (abnLookup.status !== 'done' || !abnLookup.result.ok) return;
    const r = abnLookup.result;
    if (lastFilled.current === r.abn) return;
    lastFilled.current = r.abn;
    setForm((f) => ({
      ...f,
      legal_entity_name: r.legalEntityName ?? f.legal_entity_name,
      address_state: r.addressState ?? f.address_state,
      address_postcode: r.addressPostcode ?? f.address_postcode,
    }));
  }, [abnLookup]);

  function missingFromForm(): string[] {
    const missing: string[] = [];
    if (!form.abn.trim()) missing.push('abn');
    if (!form.primary_contact_name.trim()) missing.push('primary_contact_name');
    if (!form.contact_role.trim()) missing.push('contact_role');
    if (!form.contact_phone.trim()) missing.push('contact_phone');
    if (!form.contact_email.trim()) missing.push('contact_email');
    if (!form.employee_count.trim()) missing.push('employee_count');
    return missing;
  }

  const saveMutation = useMutation({
    mutationFn: () => {
      const payload: Record<string, unknown> = {
        abn: form.abn.trim() || null,
        trustee_info: form.trustee_info.trim() || null,
        primary_contact_name: form.primary_contact_name.trim() || null,
        contact_role: form.contact_role.trim() || null,
        contact_phone: form.contact_phone.trim() || null,
        contact_email: form.contact_email.trim() || null,
        legal_entity_name: form.legal_entity_name.trim() || null,
        address_state: form.address_state.trim() || null,
        address_postcode: form.address_postcode.trim() || null,
        suburb: form.suburb.trim() || null,
      };
      const empCount = form.employee_count.trim();
      if (empCount === '') {
        payload.employee_count = null;
      } else {
        const n = Number.parseInt(empCount, 10);
        if (!Number.isFinite(n) || n < 0) {
          throw new Error('# of employees must be a non-negative number');
        }
        payload.employee_count = n;
      }
      return companyApi.patch<{ company: Company }>(apiPrefix, payload);
    },
    onSuccess: () => {
      setError(null);
      void qc.invalidateQueries({ queryKey: meQueryKey });
      onSaved();
    },
    onError: (err) => setError(err),
  });

  const missing = missingFromForm();
  // When locked (submitted for review) the fields are read-only — let the
  // user advance through the wizard without re-saving. Otherwise gate on
  // all required fields being present.
  const canContinue = locked || missing.length === 0;

  return (
    <div className="space-y-5">
      <p className="text-sm text-slate-500">
        Confirm the details we have on file before answering the questionnaire. Required fields must
        be completed to continue.
      </p>

      <div className="rounded-xl border border-slate-200 bg-slate-50 p-4">
        <h3 className="text-xs font-semibold uppercase tracking-wide text-slate-500">
          Pre-filled by your administrator
        </h3>
        <p className="mt-1 text-xs text-slate-500">
          If anything is wrong here, contact your administrator.
        </p>
        <dl className="mt-3 grid grid-cols-1 gap-3 sm:grid-cols-2">
          <ReadOnlyRow label="Company legal name" value={company.company_name} />
          <ReadOnlyRow label="Email address" value={company.email} />
          <ReadOnlyRow
            label={companyInfoLabel(branding, 'contractorType')}
            value={company.contractor_type}
          />
          <ReadOnlyRow
            label={companyInfoLabel(branding, 'companyNumber')}
            value={company.company_number}
          />
          <ReadOnlyRow
            label={companyInfoLabel(branding, 'contractNumber')}
            value={company.contract_number}
          />
          <ReadOnlyRow
            label={companyInfoLabel(branding, 'teamRegion')}
            value={company.team_region}
          />
        </dl>
      </div>

      {locked && (
        <div className="rounded-md border border-slate-200 bg-slate-50 px-3 py-2 text-xs text-slate-600">
          Submitted — fields are locked for review.
        </div>
      )}

      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
        <Field label="ABN" required>
          <TextInput
            value={form.abn}
            disabled={locked}
            onChange={(e) => set('abn', e.target.value)}
          />
          <AbnStatusNote state={abnLookup} />
        </Field>
        <Field label="Legal entity name" hint="Auto-filled from the ABN registry">
          <TextInput value={form.legal_entity_name} disabled={locked} onChange={(e) => set('legal_entity_name', e.target.value)} />
        </Field>
        <Field label="Suburb">
          <TextInput value={form.suburb} disabled={locked} onChange={(e) => set('suburb', e.target.value)} />
        </Field>
        <Field label="State"><TextInput value={form.address_state} disabled={locked} onChange={(e) => set('address_state', e.target.value)} /></Field>
        <Field label="Postcode"><TextInput value={form.address_postcode} disabled={locked} onChange={(e) => set('address_postcode', e.target.value)} /></Field>
        <Field
          label="Trustee Name and ACN (if applicable)"
          hint="Only required if you operate under a trust."
        >
          <TextInput
            value={form.trustee_info}
            disabled={locked}
            onChange={(e) => set('trustee_info', e.target.value)}
          />
        </Field>
        <Field label={companyInfoLabel(branding, 'primaryContact')} required>
          <TextInput
            value={form.primary_contact_name}
            disabled={locked}
            onChange={(e) => set('primary_contact_name', e.target.value)}
          />
        </Field>
        <Field label="Contact role/position" required>
          <TextInput
            value={form.contact_role}
            disabled={locked}
            onChange={(e) => set('contact_role', e.target.value)}
          />
        </Field>
        <Field label="Contact phone number" required>
          <TextInput
            type="tel"
            value={form.contact_phone}
            disabled={locked}
            onChange={(e) => set('contact_phone', e.target.value)}
          />
        </Field>
        <Field label="Contact email address" required>
          <TextInput
            type="email"
            value={form.contact_email}
            disabled={locked}
            onChange={(e) => set('contact_email', e.target.value)}
          />
        </Field>
        <Field label="# of employees" required>
          <TextInput
            type="number"
            min={0}
            value={form.employee_count}
            disabled={locked}
            onChange={(e) => set('employee_count', e.target.value)}
          />
        </Field>
      </div>

      <ErrorMessage error={error} />

      <div className="flex flex-wrap items-center justify-end gap-3 pt-2">
        <span className="mr-auto text-xs text-slate-500">
          {missing.length === 0
            ? 'All required fields completed'
            : `${missing.length} required field${missing.length === 1 ? '' : 's'} remaining`}
        </span>
        <Btn
          onClick={() => (locked ? onSaved() : saveMutation.mutate())}
          loading={saveMutation.isPending}
          disabled={!canContinue}
          className="!rounded-lg !normal-case !tracking-normal !font-semibold"
          style={{ backgroundColor: 'var(--brand-primary)' }}
        >
          {locked ? 'Continue →' : isLastStep ? 'Save & submit for review →' : 'Save & continue →'}
        </Btn>
      </div>
    </div>
  );
}

function ReadOnlyRow({ label, value }: { label: string; value: string | null }) {
  return (
    <div>
      <dt className="text-xs uppercase tracking-wide text-slate-500">{label}</dt>
      <dd className="mt-0.5 text-sm text-slate-900">
        {value && value.trim().length > 0 ? (
          value
        ) : (
          <span className="italic text-slate-400">Not provided</span>
        )}
      </dd>
    </div>
  );
}

function ExpiryBanner({ submissions }: { submissions: Submission[] }) {
  const now = new Date();
  const expired: SubmissionFile[] = [];
  const expiring: SubmissionFile[] = [];
  for (const s of submissions) {
    for (const f of s.submission_files) {
      const state = expiryState(f, now);
      if (state === 'expired') expired.push(f);
      else if (state === 'expiring') expiring.push(f);
    }
  }
  if (expired.length === 0 && expiring.length === 0) return null;
  return (
    <div className="space-y-2">
      {expired.length > 0 && (
        <div className="rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-800">
          <div className="font-semibold">
            {expired.length} document{expired.length === 1 ? ' has' : 's have'} expired
          </div>
          <div className="mt-1 text-xs">Re-upload them to restore your compliance.</div>
          <ul className="mt-2 list-disc pl-5 text-xs">
            {expired.slice(0, 5).map((f) => (
              <li key={f.id}>
                {f.file_name}
                {f.expires_at ? ` — expired ${new Date(f.expires_at).toLocaleDateString()}` : ''}
              </li>
            ))}
            {expired.length > 5 && <li>…and {expired.length - 5} more</li>}
          </ul>
        </div>
      )}
      {expiring.length > 0 && (
        <div className="rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900">
          <div className="font-semibold">
            {expiring.length} document{expiring.length === 1 ? '' : 's'} expiring soon
          </div>
          <ul className="mt-2 list-disc pl-5 text-xs">
            {expiring.slice(0, 5).map((f) => (
              <li key={f.id}>
                {f.file_name}
                {f.expires_at ? ` — expires ${new Date(f.expires_at).toLocaleDateString()}` : ''}
              </li>
            ))}
            {expiring.length > 5 && <li>…and {expiring.length - 5} more</li>}
          </ul>
        </div>
      )}
    </div>
  );
}

function QuestionCard({
  question,
  submission,
  documents,
  documentsList,
  instanceOption,
  submittedAt,
}: {
  question: Question;
  submission: Submission | undefined;
  documents: Map<string, CompanyDocument>;
  documentsList: CompanyDocument[];
  instanceOption: string | null;
  submittedAt: string | null;
}) {
  const completion = questionCompletion(question, submission);
  const adoptionTargets = activeAdoptionTargets(question, submission);
  const locked =
    submission?.status === 'approved' ||
    submission?.status === 'not_applicable' ||
    isAnswerLocked(submittedAt, submission?.status ?? null);

  if (question.type === 'informational') {
    return <InformationalCard question={question} submission={submission} locked={locked} />;
  }

  if (question.type === 'image_info') {
    return <ImageInfoCard question={question} />;
  }

  return (
    <div className="rounded-xl border border-slate-200 bg-white p-5">
      <div className="mb-3 flex items-start justify-between gap-3">
        <div>
          <div className="text-sm font-medium text-slate-900">
            {question.label}
            {instanceOption && (
              <span className="ml-2 rounded-full bg-indigo-50 px-2 py-0.5 text-xs font-normal text-indigo-700">
                For: {instanceOption}
              </span>
            )}
            {question.required && <span className="ml-1 text-red-500">*</span>}
          </div>
          {question.help_text && (
            <div className="mt-0.5 text-xs text-slate-500">{question.help_text}</div>
          )}
        </div>
        {submission && <SubmissionStatusBadge status={submission.status} />}
      </div>

      {submission?.status === 'not_applicable' && (
        <div className="mb-3 rounded-md border border-slate-200 bg-slate-50 px-3 py-2 text-sm text-slate-600">
          Marked not applicable by reviewer — no action needed.
        </div>
      )}

      {submission?.reviewer_comment && (
        <div
          className={`mb-3 rounded-md border px-3 py-2 text-sm ${
            submission.status === 'rejected'
              ? 'border-orange-200 bg-orange-50 text-orange-800'
              : 'border-blue-200 bg-blue-50 text-blue-800'
          }`}
        >
          <div className="font-medium">
            {submission.status === 'rejected' ? 'Changes requested' : 'Reviewer comment'}
          </div>
          <div>{submission.reviewer_comment}</div>
          {submission.reference_doc_url && /^https?:\/\//i.test(submission.reference_doc_url) && (
            <a
              href={submission.reference_doc_url}
              target="_blank"
              rel="noopener noreferrer"
              className="mt-1 inline-block font-medium text-blue-700 hover:underline"
            >
              📎 Download example: {submission.reference_doc_name ?? 'reference document'}
            </a>
          )}
        </div>
      )}

      {question.type === 'multiselect_uploads' && (
        <div className="mb-3 rounded-md border border-slate-200 bg-slate-50 px-3 py-2 text-xs text-slate-600">
          Tick each option that applies, then upload a document for each ticked option before you
          can continue.
        </div>
      )}

      <AnswerInput question={question} submission={submission} locked={locked} />

      {question.type === 'multiselect_uploads' ? (
        <MultiselectUploadsEditor
          question={question}
          submission={submission}
          documents={documents}
          disabled={locked}
        />
      ) : (
        <>
          {(question.requires_upload ||
            question.upload_label ||
            (question.type === 'document_upload' && !question.adoptable_doc_id)) && (
            <FilesEditor question={question} submission={submission} disabled={locked} />
          )}

          {adoptionTargets.length > 0 && (
            <AdoptionsEditor
              question={question}
              submission={submission}
              targets={adoptionTargets}
              documents={documents}
              documentsList={documentsList}
              disabled={locked}
            />
          )}
        </>
      )}

      {completion && (
        <div className="mt-3 text-xs text-slate-500">{completionMessage(completion)}</div>
      )}
    </div>
  );
}

function completionMessage(
  reason: 'no_answer' | 'upload_required' | 'adoption_required' | 'video_unwatched',
): string {
  switch (reason) {
    case 'no_answer':
      return '⏳ Awaiting your answer';
    case 'upload_required':
      return '⏳ A file upload is required';
    case 'adoption_required':
      return '⏳ Adopt or upload the linked policy';
    case 'video_unwatched':
      return '⏳ Watch the video and mark it as watched';
  }
}

// ----------------------------------------------------------------------
// Informational card (read-only content, optional video)
// ----------------------------------------------------------------------

function InformationalCard({
  question,
  submission,
  locked,
}: {
  question: Question;
  submission: Submission | undefined;
  locked: boolean;
}) {
  const qc = useQueryClient();
  const { apiPrefix, meQueryKey, submissionsQueryKey, setLocalAnswer, clearLocalAnswer } =
    useWizardCtx();
  const answer = submission?.answer as { watched_at?: unknown } | null | undefined;
  const watchedAt =
    answer &&
    typeof answer === 'object' &&
    typeof (answer as { watched_at?: unknown }).watched_at === 'string'
      ? (answer as { watched_at: string }).watched_at
      : null;

  const markWatchedMutation = useMutation({
    mutationFn: (watchedAtIso: string) =>
      companyApi.put(`${apiPrefix}/submissions/${question.id}`, {
        answer: { watched_at: watchedAtIso },
      }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: submissionsQueryKey });
      void qc.invalidateQueries({ queryKey: meQueryKey });
    },
    onError: () => {
      // Save failed — undo the optimistic "watched" mark so the step gate
      // reflects that the video still needs confirming.
      clearLocalAnswer(question.id);
    },
  });

  function markWatched() {
    const iso = new Date().toISOString();
    // Reflect "watched" in the gate immediately so Continue isn't blocked while
    // the save is in flight.
    setLocalAnswer(question.id, { watched_at: iso }, null);
    markWatchedMutation.mutate(iso);
  }

  return (
    <div className="rounded-xl border border-slate-200 bg-white p-5">
      <div className="text-base font-semibold text-slate-900">{question.label}</div>
      {question.help_text && (
        <div className="mt-2 whitespace-pre-wrap text-sm leading-relaxed text-slate-700">
          {question.help_text}
        </div>
      )}

      {question.video_url && (
        <div className="mt-4">
          <VideoEmbed url={question.video_url} />
        </div>
      )}

      {question.video_url && (
        <div className="mt-4 flex flex-wrap items-center gap-3">
          {watchedAt ? (
            <span className="rounded-full bg-emerald-50 px-3 py-1 text-xs font-medium text-emerald-700">
              ✓ Marked as watched on {new Date(watchedAt).toLocaleString()}
            </span>
          ) : (
            <Btn
              size="sm"
              onClick={markWatched}
              loading={markWatchedMutation.isPending}
              disabled={locked}
            >
              I have watched the video
            </Btn>
          )}
          {question.required && !watchedAt && (
            <span className="text-xs text-slate-500">
              You must watch the video and confirm before continuing to the next step.
            </span>
          )}
          {markWatchedMutation.error && <ErrorMessage error={markWatchedMutation.error} />}
        </div>
      )}
    </div>
  );
}

function ImageInfoCard({ question }: { question: Question }) {
  return (
    <div className="rounded-xl border border-slate-200 bg-white p-5">
      <div className="text-base font-semibold text-slate-900">{question.label}</div>
      {question.image_url && (
        <div className="mt-3">
          <img
            src={question.image_url}
            alt={question.label}
            className="w-full rounded-lg border border-slate-200 object-contain"
          />
        </div>
      )}
      {question.help_text && (
        <div className="mt-3 whitespace-pre-wrap text-sm leading-relaxed text-slate-700">
          {question.help_text}
        </div>
      )}
    </div>
  );
}

function VideoEmbed({ url }: { url: string }) {
  const lower = url.toLowerCase();
  const isNativeVideo = lower.endsWith('.mp4') || lower.endsWith('.webm') || lower.endsWith('.ogg');

  if (isNativeVideo) {
    return (
      <video controls className="w-full rounded-lg border border-slate-200 bg-black">
        <source src={url} />
      </video>
    );
  }

  return (
    <div className="aspect-video w-full overflow-hidden rounded-lg border border-slate-200 bg-black">
      <iframe
        src={url}
        title="Embedded video"
        className="h-full w-full"
        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
        allowFullScreen
      />
    </div>
  );
}

// ----------------------------------------------------------------------
// Answer input
// ----------------------------------------------------------------------

function AnswerInput({
  question,
  submission,
  locked,
}: {
  question: Question;
  submission: Submission | undefined;
  locked: boolean;
}) {
  const qc = useQueryClient();
  const { apiPrefix, meQueryKey, submissionsQueryKey, setLocalAnswer, clearLocalAnswer } =
    useWizardCtx();
  const [local, setLocal] = useState<Json | null | undefined>(submission?.answer);
  const [comment, setComment] = useState(submission?.comment ?? '');
  const [touched, setTouched] = useState(false);

  const saveMutation = useMutation({
    mutationFn: (input: { answer: Json | null; comment: string | null }) =>
      companyApi.put(`${apiPrefix}/submissions/${question.id}`, input),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: submissionsQueryKey });
      void qc.invalidateQueries({ queryKey: meQueryKey });
      setTouched(false);
    },
    onError: () => {
      // The save failed, so the answer was NOT persisted. Drop the optimistic
      // overlay so the step gate reflects reality (the question reverts to
      // outstanding) and the error below tells the company to retry.
      clearLocalAnswer(question.id);
    },
  });

  // Report what the user entered to the wizard immediately, then persist it.
  // The gate reads the reported value at once, so a freshly-answered question
  // is never listed as "still required" while the PUT is in flight.
  function report(answer: Json | null) {
    setLocalAnswer(question.id, answer, comment.trim() || null);
  }
  function commit(answer: Json | null) {
    report(answer);
    saveMutation.mutate({ answer, comment: comment.trim() || null });
  }

  if (question.type === 'document_adopt' || question.type === 'document_upload') {
    return null; // handled by AdoptionsEditor / FilesEditor below
  }

  const saveError = saveMutation.error ? (
    <div className="mt-2">
      <ErrorMessage error={saveMutation.error} />
    </div>
  ) : null;

  let body: React.ReactNode;

  if (question.type === 'signature') {
    body = (
      <SignatureInput
        submission={submission}
        locked={locked}
        saving={saveMutation.isPending}
        onSave={(value) => {
          setLocal(value as unknown as Json);
          commit(value as unknown as Json);
        }}
        onClear={() => {
          setLocal(null);
          commit(null);
        }}
      />
    );
  } else if (question.type === 'yesno') {
    const value = (local ?? submission?.answer) as boolean | null | undefined;
    body = (
      <div className="flex gap-2">
        {[
          { v: true, label: 'Yes' },
          { v: false, label: 'No' },
        ].map((o) => (
          <button
            key={String(o.v)}
            disabled={locked}
            onClick={() => {
              setLocal(o.v);
              commit(o.v);
            }}
            className={`rounded-lg border px-4 py-2 text-sm font-medium transition ${
              value === o.v
                ? 'border-transparent text-white'
                : 'border-slate-200 bg-white text-slate-700 hover:bg-slate-50'
            } ${locked ? 'opacity-60' : ''}`}
            style={value === o.v ? { backgroundColor: 'var(--brand-accent)' } : {}}
          >
            {o.label}
          </button>
        ))}
      </div>
    );
  } else if (question.type === 'select') {
    const value = (local ?? submission?.answer) as string | null | undefined;
    body = (
      <Select
        disabled={locked}
        value={value ?? ''}
        onChange={(e) => {
          const v = e.target.value || null;
          setLocal(v);
          commit(v);
        }}
      >
        <option value="">— Select —</option>
        {(question.options ?? []).map((o) => (
          <option key={o} value={o}>
            {o}
          </option>
        ))}
      </Select>
    );
  } else if (question.type === 'multiselect' || question.type === 'multiselect_uploads') {
    const value = ((local ?? submission?.answer) as string[] | null | undefined) ?? [];
    body = (
      <div className="grid gap-2 sm:grid-cols-2">
        {(question.options ?? []).map((o) => {
          const checked = value.includes(o);
          return (
            <label
              key={o}
              className={`flex items-center gap-2 rounded-lg border px-3 py-2 text-sm ${
                checked ? 'border-current' : 'border-slate-200'
              } ${locked ? 'opacity-60' : 'cursor-pointer hover:bg-slate-50'}`}
              style={checked ? { color: 'var(--brand-accent)' } : {}}
            >
              <input
                type="checkbox"
                disabled={locked}
                checked={checked}
                onChange={() => {
                  const next = checked ? value.filter((v) => v !== o) : [...value, o];
                  setLocal(next);
                  commit(next);
                }}
              />
              <span className="text-slate-700">{o}</span>
            </label>
          );
        })}
      </div>
    );
  } else {
    // text
    const value = (local ?? submission?.answer ?? '') as string;
    body = (
      <div>
        <TextArea
          disabled={locked}
          value={String(value)}
          onChange={(e) => {
            setLocal(e.target.value);
            setTouched(true);
            // Reflect typing in the gate right away so a single "Continue"
            // click advances; the value is persisted on blur below.
            report(e.target.value);
          }}
          onBlur={() => {
            if (touched) commit(value);
          }}
        />
        <div className="mt-2 flex items-center gap-2">
          <input
            type="text"
            placeholder="Optional comment"
            value={comment}
            disabled={locked}
            onChange={(e) => setComment(e.target.value)}
            onBlur={() => {
              if (comment !== (submission?.comment ?? '')) {
                commit(value);
              }
            }}
            className="block w-full rounded-md border border-slate-200 bg-white px-3 py-1.5 text-sm placeholder:text-slate-400"
          />
          {saveMutation.isPending && <span className="text-xs text-slate-400">Saving…</span>}
        </div>
      </div>
    );
  }

  return (
    <>
      {body}
      {saveError}
    </>
  );
}

// ----------------------------------------------------------------------
// Signature
// ----------------------------------------------------------------------

type SignatureAnswerValue = {
  signature_data_url: string;
  signer_name: string;
  signed_at: string;
};

function isSignatureAnswer(value: unknown): value is SignatureAnswerValue {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return false;
  const v = value as Record<string, unknown>;
  return (
    typeof v.signature_data_url === 'string' &&
    typeof v.signer_name === 'string' &&
    typeof v.signed_at === 'string'
  );
}

function SignatureInput({
  submission,
  locked,
  saving,
  onSave,
  onClear,
}: {
  submission: Submission | undefined;
  locked: boolean;
  saving: boolean;
  onSave: (value: SignatureAnswerValue) => void;
  onClear: () => void;
}) {
  const existing = isSignatureAnswer(submission?.answer) ? submission!.answer : null;
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const drawingRef = useRef(false);
  const lastPointRef = useRef<{ x: number; y: number } | null>(null);
  const hasStrokeRef = useRef(false);
  const [signerName, setSignerName] = useState(existing?.signer_name ?? '');
  const [hasDrawn, setHasDrawn] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ratio = window.devicePixelRatio || 1;
    const rect = canvas.getBoundingClientRect();
    canvas.width = rect.width * ratio;
    canvas.height = rect.height * ratio;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    ctx.scale(ratio, ratio);
    ctx.lineWidth = 2;
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';
    ctx.strokeStyle = '#0f172a';
  }, []);

  function pointerPos(e: React.PointerEvent<HTMLCanvasElement>): { x: number; y: number } {
    const canvas = canvasRef.current!;
    const rect = canvas.getBoundingClientRect();
    return { x: e.clientX - rect.left, y: e.clientY - rect.top };
  }

  function startStroke(e: React.PointerEvent<HTMLCanvasElement>) {
    if (locked) return;
    const canvas = canvasRef.current;
    if (!canvas) return;
    canvas.setPointerCapture(e.pointerId);
    drawingRef.current = true;
    lastPointRef.current = pointerPos(e);
  }

  function drawStroke(e: React.PointerEvent<HTMLCanvasElement>) {
    if (!drawingRef.current) return;
    const canvas = canvasRef.current;
    const ctx = canvas?.getContext('2d');
    const last = lastPointRef.current;
    if (!canvas || !ctx || !last) return;
    const p = pointerPos(e);
    ctx.beginPath();
    ctx.moveTo(last.x, last.y);
    ctx.lineTo(p.x, p.y);
    ctx.stroke();
    lastPointRef.current = p;
    hasStrokeRef.current = true;
    if (!hasDrawn) setHasDrawn(true);
  }

  function endStroke(e: React.PointerEvent<HTMLCanvasElement>) {
    drawingRef.current = false;
    lastPointRef.current = null;
    const canvas = canvasRef.current;
    if (canvas && canvas.hasPointerCapture(e.pointerId)) {
      canvas.releasePointerCapture(e.pointerId);
    }
  }

  function clearCanvas() {
    const canvas = canvasRef.current;
    const ctx = canvas?.getContext('2d');
    if (!canvas || !ctx) return;
    const ratio = window.devicePixelRatio || 1;
    ctx.clearRect(0, 0, canvas.width / ratio, canvas.height / ratio);
    hasStrokeRef.current = false;
    setHasDrawn(false);
  }

  function handleSave() {
    setError(null);
    if (!hasStrokeRef.current) {
      setError('Please draw your signature.');
      return;
    }
    const name = signerName.trim();
    if (!name) {
      setError('Please type your name.');
      return;
    }
    const canvas = canvasRef.current;
    if (!canvas) return;
    const dataUrl = canvas.toDataURL('image/png');
    onSave({
      signature_data_url: dataUrl,
      signer_name: name,
      signed_at: new Date().toISOString(),
    });
  }

  function handleClear() {
    setError(null);
    clearCanvas();
    setSignerName('');
    onClear();
  }

  if (existing) {
    return (
      <div className="space-y-3">
        <div className="rounded-lg border border-slate-200 bg-white p-3">
          <img
            src={existing.signature_data_url}
            alt="Signature"
            className="h-32 w-full object-contain"
          />
        </div>
        <div className="text-sm text-slate-700">
          <div>
            Signed by <span className="font-medium">{existing.signer_name}</span>
          </div>
          <div className="text-xs text-slate-500">
            {new Date(existing.signed_at).toLocaleString()}
          </div>
        </div>
        {!locked && (
          <Btn variant="ghost" size="sm" onClick={handleClear} loading={saving}>
            Clear &amp; re-sign
          </Btn>
        )}
      </div>
    );
  }

  return (
    <div className="space-y-3">
      <div>
        <div className="mb-1 text-xs font-medium text-slate-600">Draw your signature below</div>
        <canvas
          ref={canvasRef}
          onPointerDown={startStroke}
          onPointerMove={drawStroke}
          onPointerUp={endStroke}
          onPointerCancel={endStroke}
          onPointerLeave={endStroke}
          className="h-40 w-full touch-none rounded-lg border border-dashed border-slate-300 bg-white"
          style={{ cursor: locked ? 'not-allowed' : 'crosshair' }}
        />
      </div>
      <Field label="Full name" required>
        <input
          type="text"
          value={signerName}
          disabled={locked}
          onChange={(e) => setSignerName(e.target.value)}
          placeholder="Type your full name"
          className="block w-full rounded-md border border-slate-200 bg-white px-3 py-1.5 text-sm placeholder:text-slate-400"
        />
      </Field>
      <div className="flex flex-wrap items-center gap-2">
        <Btn size="sm" onClick={handleSave} loading={saving} disabled={locked}>
          Save signature
        </Btn>
        <Btn
          size="sm"
          variant="ghost"
          onClick={() => {
            setError(null);
            clearCanvas();
          }}
          disabled={locked || !hasDrawn}
        >
          Clear drawing
        </Btn>
        <span className="text-xs text-slate-400">
          Signed-at timestamp is recorded automatically.
        </span>
      </div>
      {error && <div className="text-xs text-red-600">{error}</div>}
    </div>
  );
}

// ----------------------------------------------------------------------
// Files
// ----------------------------------------------------------------------

function UploadedFileRow({
  questionId,
  file,
  disabled,
}: {
  questionId: string;
  file: SubmissionFile;
  disabled: boolean;
}) {
  const qc = useQueryClient();
  const { apiPrefix, submissionsQueryKey } = useWizardCtx();
  const [error, setError] = useState<unknown>(null);
  const [saving, setSaving] = useState(false);
  const [expiryDraft, setExpiryDraft] = useState(() => toDateInput(file.expires_at));
  useEffect(() => {
    setExpiryDraft(toDateInput(file.expires_at));
  }, [file.expires_at]);

  const state = expiryState(file);

  async function setExpiry(value: string) {
    const iso = fromDateInput(value);
    setError(null);
    setSaving(true);
    try {
      await companyApi.patch(`${apiPrefix}/submissions/${questionId}/files/${file.id}`, {
        expires_at: iso,
      });
      await qc.invalidateQueries({ queryKey: submissionsQueryKey });
    } catch (err) {
      setError(err);
    } finally {
      setSaving(false);
    }
  }

  async function downloadFile() {
    const res = await companyApi
      .get<{ url: string }>(`${apiPrefix}/submissions/${questionId}/files/${file.id}/download-url`)
      .catch(() => null);
    if (res?.url) window.open(res.url, '_blank', 'noopener');
  }

  async function deleteFile() {
    setError(null);
    try {
      await companyApi.delete(`${apiPrefix}/submissions/${questionId}/files/${file.id}`);
      await qc.invalidateQueries({ queryKey: submissionsQueryKey });
    } catch (err) {
      setError(err);
    }
  }

  return (
    <li className="rounded-md bg-slate-50 px-3 py-2 text-sm">
      <div className="flex items-center justify-between gap-3">
        <button onClick={() => void downloadFile()} className="text-blue-600 hover:underline">
          📎 {file.file_name}
        </button>
        <div className="flex items-center gap-3">
          <ExpiryBadge state={state} />
          {!disabled && (
            <button
              onClick={() => void deleteFile()}
              className="text-xs text-red-600 hover:underline"
            >
              Remove
            </button>
          )}
        </div>
      </div>
      {!disabled && (
        <div className="mt-2 flex flex-wrap items-center gap-2 text-xs text-slate-600">
          <label className="flex items-center gap-1.5">
            <span>Expires:</span>
            <input
              type="date"
              value={expiryDraft}
              onChange={(e) => setExpiryDraft(e.target.value)}
              onBlur={() => {
                if (expiryDraft !== toDateInput(file.expires_at)) void setExpiry(expiryDraft);
              }}
              className="rounded border border-slate-300 px-2 py-1 text-xs"
            />
          </label>
          {file.expires_at && (
            <button
              type="button"
              onClick={() => { setExpiryDraft(''); void setExpiry(''); }}
              disabled={saving}
              className="text-xs text-slate-500 hover:underline"
            >
              Clear
            </button>
          )}
          <span className="text-slate-400">Leave blank if this document does not expire.</span>
        </div>
      )}
      <ErrorMessage error={error} />
    </li>
  );
}

function ExpiryBadge({ state }: { state: ReturnType<typeof expiryState> }) {
  if (state === 'none' || state === 'ok') return null;
  if (state === 'expired') {
    return (
      <span className="rounded-full bg-red-100 px-2 py-0.5 text-xs font-medium text-red-700">
        Expired
      </span>
    );
  }
  return (
    <span className="rounded-full bg-amber-100 px-2 py-0.5 text-xs font-medium text-amber-800">
      Expiring soon
    </span>
  );
}

function FilesEditor({
  question,
  submission,
  disabled,
}: {
  question: Question;
  submission: Submission | undefined;
  disabled: boolean;
}) {
  const qc = useQueryClient();
  const { apiPrefix, meQueryKey, submissionsQueryKey } = useWizardCtx();
  const [error, setError] = useState<unknown>(null);
  const [uploading, setUploading] = useState(false);

  const files = submission?.submission_files ?? [];

  async function uploadFiles(list: FileList) {
    setError(null);
    setUploading(true);
    try {
      for (const file of Array.from(list)) {
        const presigned = await companyApi.post<{ path: string; url: string }>(
          `${apiPrefix}/submissions/${question.id}/upload-url`,
          { file_name: file.name, mime_type: file.type || 'application/octet-stream' },
        );
        await uploadFileToSignedUrl(presigned.url, file);
        await companyApi.post(`${apiPrefix}/submissions/${question.id}/files`, {
          file_path: presigned.path,
          file_name: file.name,
          size_bytes: file.size,
          mime_type: file.type || 'application/octet-stream',
        });
      }
      await qc.invalidateQueries({ queryKey: submissionsQueryKey });
      await qc.invalidateQueries({ queryKey: meQueryKey });
    } catch (err) {
      setError(err instanceof ApiError ? err : err);
    } finally {
      setUploading(false);
    }
  }

  return (
    <div className="mt-3">
      <Field label={question.upload_label ?? 'Attachments'} required={question.requires_upload}>
        <FileDrop
          onFiles={uploadFiles}
          disabled={disabled || uploading}
          hint={uploading ? 'Uploading…' : undefined}
        />
      </Field>
      {files.length > 0 && (
        <ul className="mt-2 space-y-2">
          {files.map((f) => (
            <UploadedFileRow key={f.id} questionId={question.id} file={f} disabled={disabled} />
          ))}
        </ul>
      )}
      <ErrorMessage error={error} />
    </div>
  );
}

// ----------------------------------------------------------------------
// Per-option uploads (multiselect_uploads)
// ----------------------------------------------------------------------

function MultiselectUploadsEditor({
  question,
  submission,
  documents,
  disabled,
}: {
  question: Question;
  submission: Submission | undefined;
  documents: Map<string, CompanyDocument>;
  disabled: boolean;
}) {
  const selected = (submission?.answer as string[] | null | undefined) ?? [];
  if (selected.length === 0) return null;

  const triggerByOption = new Map<string, { documentId: string; adoptOnly: boolean }>();
  for (const t of question.adoption_triggers) {
    triggerByOption.set(t.trigger_value, {
      documentId: t.document_id,
      adoptOnly: t.adopt_only,
    });
  }

  return (
    <div className="mt-4 space-y-3">
      {selected.map((opt) => (
        <OptionUploadRow
          key={opt}
          question={question}
          submission={submission}
          option={opt}
          trigger={triggerByOption.get(opt) ?? null}
          documents={documents}
          disabled={disabled}
        />
      ))}
    </div>
  );
}

function OptionUploadRow({
  question,
  submission,
  option,
  trigger,
  documents,
  disabled,
}: {
  question: Question;
  submission: Submission | undefined;
  option: string;
  trigger: { documentId: string; adoptOnly: boolean } | null;
  documents: Map<string, CompanyDocument>;
  disabled: boolean;
}) {
  const qc = useQueryClient();
  const { apiPrefix, meQueryKey, submissionsQueryKey } = useWizardCtx();
  const [error, setError] = useState<unknown>(null);
  const [uploading, setUploading] = useState(false);

  const files = (submission?.submission_files ?? []).filter((f) => f.option_key === option);
  const adoption = (submission?.submission_adoptions ?? []).find(
    (a) => a.option_key === option && trigger && a.document_id === trigger.documentId,
  );

  const adoptableDoc = trigger ? documents.get(trigger.documentId) : undefined;

  async function uploadFiles(list: FileList) {
    setError(null);
    setUploading(true);
    try {
      for (const file of Array.from(list)) {
        const presigned = await companyApi.post<{ path: string; url: string }>(
          `${apiPrefix}/submissions/${question.id}/upload-url`,
          {
            file_name: file.name,
            mime_type: file.type || 'application/octet-stream',
            option_key: option,
          },
        );
        await uploadFileToSignedUrl(presigned.url, file);
        await companyApi.post(`${apiPrefix}/submissions/${question.id}/files`, {
          file_path: presigned.path,
          file_name: file.name,
          size_bytes: file.size,
          mime_type: file.type || 'application/octet-stream',
          option_key: option,
        });
      }
      await qc.invalidateQueries({ queryKey: submissionsQueryKey });
      await qc.invalidateQueries({ queryKey: meQueryKey });
    } catch (err) {
      setError(err instanceof ApiError ? err : err);
    } finally {
      setUploading(false);
    }
  }

  async function adopt() {
    if (!trigger) return;
    setError(null);
    try {
      await companyApi.put(
        `${apiPrefix}/submissions/${question.id}/adoptions/${trigger.documentId}`,
        { mode: 'adopted', option_key: option },
      );
      await qc.invalidateQueries({ queryKey: submissionsQueryKey });
    } catch (err) {
      setError(err);
    }
  }

  async function clearAdoption() {
    if (!trigger) return;
    try {
      await companyApi.delete(
        `${apiPrefix}/submissions/${question.id}/adoptions/${trigger.documentId}`,
      );
      await qc.invalidateQueries({ queryKey: submissionsQueryKey });
    } catch (err) {
      setError(err);
    }
  }

  async function downloadStandard() {
    if (!trigger) return;
    const res = await companyApi
      .get<{ url: string }>(`${apiPrefix}/documents/${trigger.documentId}/download-url`)
      .catch(() => null);
    if (res?.url) window.open(res.url, '_blank', 'noopener');
  }

  const adopted = adoption?.mode === 'adopted';
  const satisfied = adopted || files.length > 0;

  return (
    <div className="rounded-lg border border-slate-200 bg-slate-50 p-4">
      <div className="flex items-center justify-between gap-2">
        <div className="flex items-center gap-2">
          <div className="text-sm font-medium text-slate-900">{option}</div>
          {satisfied ? (
            <span className="rounded-full bg-emerald-50 px-2 py-0.5 text-[11px] font-medium text-emerald-700">
              ✓ Provided
            </span>
          ) : (
            <span className="rounded-full bg-amber-50 px-2 py-0.5 text-[11px] font-medium text-amber-800">
              Document required
            </span>
          )}
        </div>
        {trigger && adoptableDoc && (
          <button
            onClick={() => void downloadStandard()}
            className="text-xs text-blue-600 hover:underline"
          >
            View standard: {adoptableDoc.name}
          </button>
        )}
      </div>

      {trigger && (
        <div className="mt-2 flex flex-wrap items-center gap-2">
          <Btn
            size="sm"
            variant={adopted ? 'primary' : 'secondary'}
            disabled={disabled}
            onClick={() => void adopt()}
          >
            {adopted ? '✓ Adopted' : 'Adopt this policy'}
          </Btn>
          {adopted && !disabled && (
            <button
              onClick={() => void clearAdoption()}
              className="text-xs text-red-600 hover:underline"
            >
              Clear adoption
            </button>
          )}
          {!trigger.adoptOnly && <span className="text-xs text-slate-400">or upload below</span>}
        </div>
      )}

      {(!trigger || !trigger.adoptOnly) && (
        <div className="mt-3">
          <FileDrop
            onFiles={uploadFiles}
            disabled={disabled || uploading}
            hint={uploading ? 'Uploading…' : 'Upload one or more documents for this option'}
          />
          {files.length > 0 && (
            <ul className="mt-2 space-y-2">
              {files.map((f) => (
                <UploadedFileRow key={f.id} questionId={question.id} file={f} disabled={disabled} />
              ))}
            </ul>
          )}
        </div>
      )}
      <ErrorMessage error={error} />
    </div>
  );
}

// ----------------------------------------------------------------------
// Adoptions (document_adopt + adoption triggers)
// ----------------------------------------------------------------------

function AdoptionsEditor({
  question,
  submission,
  targets,
  documents,
  documentsList,
  disabled,
}: {
  question: Question;
  submission: Submission | undefined;
  targets: { documentId: string; adoptOnly: boolean }[];
  documents: Map<string, CompanyDocument>;
  documentsList: CompanyDocument[];
  disabled: boolean;
}) {
  return (
    <div className="mt-4 space-y-3">
      {targets.map((t) => (
        <DocumentAdoptionRow
          key={t.documentId}
          question={question}
          submission={submission}
          documentId={t.documentId}
          adoptOnly={t.adoptOnly}
          document={documents.get(t.documentId)}
          allDocs={documentsList}
          disabled={disabled}
        />
      ))}
    </div>
  );
}

function DocumentAdoptionRow({
  question,
  submission,
  documentId,
  adoptOnly,
  document,
  allDocs,
  disabled,
}: {
  question: Question;
  submission: Submission | undefined;
  documentId: string;
  adoptOnly: boolean;
  document: CompanyDocument | undefined;
  allDocs: CompanyDocument[];
  disabled: boolean;
}) {
  const qc = useQueryClient();
  const { apiPrefix, meQueryKey, submissionsQueryKey } = useWizardCtx();
  const [error, setError] = useState<unknown>(null);
  const [uploading, setUploading] = useState(false);

  const adoption = submission?.submission_adoptions.find((a) => a.document_id === documentId);

  const adoptMutation = useMutation({
    mutationFn: () =>
      companyApi.put(`${apiPrefix}/submissions/${question.id}/adoptions/${documentId}`, {
        mode: 'adopted',
      }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: submissionsQueryKey });
      void qc.invalidateQueries({ queryKey: meQueryKey });
    },
  });

  const deleteMutation = useMutation({
    mutationFn: () =>
      companyApi.delete(`${apiPrefix}/submissions/${question.id}/adoptions/${documentId}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: submissionsQueryKey }),
  });

  async function downloadDoc() {
    const res = await companyApi
      .get<{ url: string }>(`${apiPrefix}/documents/${documentId}/download-url`)
      .catch(() => null);
    if (res?.url) window.open(res.url, '_blank', 'noopener');
  }

  async function uploadOwn(files: FileList) {
    setError(null);
    setUploading(true);
    try {
      const file = files.item(0);
      if (!file) return;
      const presigned = await companyApi.post<{ path: string; url: string }>(
        `${apiPrefix}/submissions/${question.id}/upload-url`,
        { file_name: file.name, mime_type: file.type || 'application/octet-stream' },
      );
      await uploadFileToSignedUrl(presigned.url, file);
      await companyApi.put(`${apiPrefix}/submissions/${question.id}/adoptions/${documentId}`, {
        mode: 'uploaded',
        file_path: presigned.path,
      });
      await qc.invalidateQueries({ queryKey: submissionsQueryKey });
    } catch (err) {
      setError(err instanceof ApiError ? err : err);
    } finally {
      setUploading(false);
    }
  }

  const docName = document?.name ?? allDocs.find((d) => d.id === documentId)?.name ?? 'Document';

  return (
    <div className="rounded-lg border border-slate-200 bg-slate-50 p-4">
      <div className="flex items-start justify-between gap-3">
        <div>
          <div className="text-sm font-medium text-slate-900">{docName}</div>
          <div className="mt-0.5 text-xs text-slate-500">
            {adoptOnly
              ? 'You must adopt this standard policy.'
              : 'Adopt the standard policy or upload your own equivalent.'}
          </div>
        </div>
        <button
          onClick={() => void downloadDoc()}
          className="text-xs text-blue-600 hover:underline"
        >
          View standard
        </button>
      </div>

      <div className="mt-3 flex flex-wrap items-center gap-2">
        <Btn
          size="sm"
          variant={adoption?.mode === 'adopted' ? 'primary' : 'secondary'}
          disabled={disabled}
          loading={adoptMutation.isPending}
          onClick={() => adoptMutation.mutate()}
        >
          {adoption?.mode === 'adopted' ? '✓ Adopted' : 'Adopt this policy'}
        </Btn>
        {!adoptOnly && (
          <>
            <span className="text-xs text-slate-400">or</span>
            <label
              className={`text-xs ${disabled ? 'opacity-50' : 'cursor-pointer text-blue-600 hover:underline'}`}
            >
              <input
                type="file"
                className="hidden"
                disabled={disabled || uploading}
                onChange={(e) => {
                  if (e.target.files?.length) uploadOwn(e.target.files);
                  e.target.value = '';
                }}
              />
              {uploading
                ? 'Uploading…'
                : adoption?.mode === 'uploaded'
                  ? 'Replace upload'
                  : 'Upload your own'}
            </label>
            {adoption?.mode === 'uploaded' && adoption.file_path && (
              <span className="ml-2 text-xs text-slate-500">
                {adoption.file_path.split('/').pop()}
              </span>
            )}
          </>
        )}
        {adoption && !disabled && (
          <button
            onClick={() => deleteMutation.mutate()}
            className="ml-auto text-xs text-red-600 hover:underline"
          >
            Clear
          </button>
        )}
      </div>
      <ErrorMessage error={error} />
    </div>
  );
}
